import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form8995 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 8995 | Qualified Business Income Deduction Simplified"
          description="Learn how to file Form 8995 for the Qualified Business Income Deduction. Simplify your 990-T filing and claim the QBID with TaxZerOne's seamless e-file process."
          keywords=""
          canonical="resources/tax-exempt/form-8995-qualified-business-income-deduction/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 8995,{' '}
          <span className="highlight-container">
            <span className="highlight">
              Qualified Business Income Deduction{' '}
            </span>
          </span>
          Simplified Computation
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-8995-qualified-business-income-deduction/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 8995 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Purpose: Form 8995
                        </strong>{' '}
                        is used to calculate the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Qualified Business Income (QBI) deduction
                        </strong>{' '}
                        for eligible individuals, estates, and trusts with
                        qualified business income.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        Eligible taxpayers generally use{' '}
                        <strong style={{ fontWeight: 600 }}> Form 8995</strong>{' '}
                        when they have{' '}
                        <strong style={{ fontWeight: 600 }}>
                          QBI, qualified REIT dividends, or qualified PTP income
                          or loss
                        </strong>{' '}
                        taxable-income requirements.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?{' '}
                        </strong>
                        Form 8995 calculates the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          QBI deduction,
                        </strong>{' '}
                        including qualified business income or loss, qualified
                        REIT dividends, qualified PTP income, taxable-income
                        limitations, and applicable carryforwards.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Supported Tax Returns:{' '}
                        </strong>{' '}
                        Form 8995 may be used with{' '}
                        <strong style={{ fontWeight: 600 }}>Form 1041</strong>{' '}
                        and{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-t/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 990-T
                        </Link>{' '}
                        <strong style={{ fontWeight: 600 }}>
                          for eligible trust filers.
                        </strong>{' '}
                        For a trust filing Form 990-T, the QBI deduction is
                        reported when the trust meets the applicable
                        <strong style={{ fontWeight: 600 }}>
                          Section 199A{' '}
                        </strong>
                        requirements.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 8995?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Form 8995?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to File Form 8995?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="what"
                >
                  What is Form 8995?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>Form 8995</strong>,{' '}
                  <i>
                    Qualified Business Income Deduction Simplified Computation
                  </i>
                  , is used by eligible individuals, trusts, and estates to
                  calculate and claim the Qualified Business Income Deduction.
                  The QBID allows certain taxpayers to deduct up to 20% of their
                  qualified business income (QBI) from partnerships, S
                  corporations, sole proprietorships, and some trusts and
                  estates.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  The deduction is limited to those whose taxable income falls
                  below the specified threshold and is subject to further
                  limitations based on the nature of the business and wages
                  paid.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who must file Form 8995?
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Taxpayers who meet the following criteria must file Form 8995:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Individuals, trusts, and estates:
                    </strong>{' '}
                    Those with taxable income below the threshold (currently
                    $197,300 for single filers and $394,600 for joint filers in
                    2025) and with income from qualified businesses or
                    pass-through entities.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Owners of partnerships, S corporations, or sole
                      proprietorships:
                    </strong>{' '}
                    Those who receive qualified business income (QBI) from these
                    entities.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Tax-exempt organizations filing Form 990-T:
                    </strong>{' '}
                    Organizations with unrelated business taxable income (UBTI)
                    may use Form 8995 to claim a QBID for their unrelated
                    business activities.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your taxable income exceeds the threshold, you may need to
                  file <strong style={{ fontWeight: 600 }}>Form 8995-A</strong>,
                  the expanded version of this form, to calculate the QBID.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Streamline Your 990-T Filing with TaxZerone!
                      </Typography>
                      <Typography variant="body2">
                        Make your e-filing process simple by clicking the button
                        below.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form8995`}
                      >
                        E-File Form 990-T
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                {/* <Alert severity="warning" icon={false} sx={{ mb: 4 }} >
                  <strong style={{fontWeight:600}} >Note:</strong> The deadline is extended to the next business day
                  if the due date falls on a weekend or a legal holiday.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="howto"
                >
                  How to File Form 8995?
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8995-img.webp" />
                  <source srcSet="/assets/images/f8995-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8995-img.png"
                    loading="lazy"
                    alt="Form 8995 Qualified Business Income Deduction Simplified Computation"
                    width={760}
                    height={211}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In this Form 8995,{' '}
                  <span style={{ fontWeight: 600 }}> Enter your name</span>{' '}
                  exactly as shown on your tax return. If you are filing
                  jointly, include both spouses’ names.
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  Next, provide your Taxpayer Identification Number (TIN), like:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Social Security Number (SSN) OR</li>
                  <li>Employer Identification Number (EIN). </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    mb: 2,
                    borderLeft: '3px solid rgb(102, 60, 0)',
                    fontStyle: 'italic',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> Form 8995
                  can be used only if you have qualified business income (QBI),
                  qualified REIT dividends, or qualified PTP income or loss, and
                  your taxable income before the QBI deduction is $197,300 or
                  less ($394,600 for married couples filing jointly). If your
                  income is above these limits, you must use{' '}
                  <span style={{ fontWeight: 600 }}> Form 8995-A</span>.
                </Alert>
                <picture>
                  <source srcSet="/assets/images/f8995-line1-img.webp" />
                  <source srcSet="/assets/images/f8995-line1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8995-line1-img.png"
                    loading="lazy"
                    alt="Form 8995 Qualified Business Income Deduction Simplified Computation Line1 to Line 10"
                    width={760}
                    height={415}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    On Line 1(a), enter the{' '}
                    <span style={{ fontWeight: 600 }}>
                      name of your qualified trade or business
                    </span>
                    . If you combine multiple businesses into one aggregation,
                    enter the aggregation name instead, such as “Aggregation 1”
                    or “Aggregation 2,” and leave Line 1(b) blank if applicable.
                  </li>
                  <li>
                    If you are using the{' '}
                    <span style={{ fontWeight: 600 }}>
                      rental real estate safe harbor
                    </span>{' '}
                    under Rev. Proc. 2019-38, enter the enterprise name exactly
                    as listed on your safe harbor statement, such as “Enterprise
                    1.”
                  </li>
                  <li>
                    On Line 1(b), enter the business EIN. If you do not have an
                    EIN, use your SSN or ITIN instead. Single-member LLC owners
                    should enter the LLC’s EIN, if available.
                  </li>
                  <li>
                    On Line 1(c), enter the net qualified business income (QBI)
                    or loss for that business. Do not include suspended losses
                    or deductions that are not allowed in the current tax year.
                  </li>
                </Box>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid #0288d1' }}
                >
                  <strong style={{ fontWeight: 600 }}>Important: :</strong> If
                  you aggregate multiple trades or businesses, attach Schedule B
                  (Form 8995-A) or a similar schedule.
                </Alert>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1">
                  If you have more than five qualified trades or businesses,
                  attach a separate statement listing:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>the business name,</li>
                  <li>taxpayer identification number, and </li>
                  <li>related income or loss.</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Include the totals from those additional businesses on Line 2.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 3
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter any qualified business loss carry-forward from prior
                    years that is allowed in the current tax year. This applies
                    even if the business no longer exists.{' '}
                  </li>
                  <li>
                    Do not include losses or deductions that are still suspended
                    under other IRS rules, as those must be tracked separately
                    until they become allowable.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 4
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If your total qualified business income results in a net
                    loss for the year, you generally cannot claim the QBI
                    deduction unless you also have qualified{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      REIT dividends or qualified PTP income
                    </span>
                    .
                  </li>
                  <li>
                    The loss will carry forward to the next tax year and may
                    reduce future QBI deductions.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For Line 5, multiply the amount on Line 4 by{' '}
                  <span style={{ fontWeight: 600 }}>20% (0.20)</span>. This
                  calculates your qualified business income component for the
                  QBI deduction.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter all qualified REIT dividends and qualified publicly
                  traded partnership (PTP) income or loss on this line.{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Report income as a positive number.</li>
                  <li>Report losses as a negative number. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 7
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter any qualified PTP loss carry-forward from prior years
                    that is allowed in the current tax year. This applies even
                    if you no longer own the PTP or if the partnership no longer
                    exists.
                  </li>
                  <li>
                    Do not include losses or deductions that are still suspended
                    under other IRS rules. Those amounts must be tracked
                    separately until they become deductible.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 8
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If the total on Line 8 is negative, the loss will carry
                    forward to the next tax year.
                  </li>
                  <li>
                    This carry-forward only affects your future QBI deduction
                    calculation and does not change how the loss is treated
                    under other tax rules.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply the figure on{' '}
                  <span style={{ fontWeight: 600 }}>Line 8 by 20% (0.20)</span>{' '}
                  to determine your REIT and PTP component.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Combine the figures on{' '}
                  <span style={{ fontWeight: 600 }}>Lines 5</span> and{' '}
                  <span style={{ fontWeight: 600 }}>Line 9</span> to determine
                  your qualified business income deduction before the income
                  limit.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8995-line11-img.webp" />
                  <source srcSet="/assets/images/f8995-line11-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8995-line11-img.png"
                    loading="lazy"
                    alt="Form 8995 Qualified Business Income Deduction Simplified Computation Line11 to Line 17"
                    width={760}
                    height={164}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter your taxable income figured before the QBI deduction.
                  The figure should come from your tax form and be reduced by
                  your applicable deductions according to the instructions of
                  the Internal Revenue Service. For instance,{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Forms 1040, 1041, and{' '}
                    <Link
                      href="/resources/tax-exempt/form-990-t-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      990-T
                    </Link>
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 12
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter your qualified dividends and net capital gain. The
                    amount you use depends on the form you file, such as: Form
                    1040 or 1040-SR, Form 1041, Form 1041-N, or Form 990-T.
                  </li>
                  <li>
                    If your{' '}
                    <span style={{ fontWeight: 600 }}>
                      net capital gain is zero or less,
                    </span>{' '}
                    include only the qualified{' '}
                    <span style={{ fontWeight: 600 }}>dividends amount</span>.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 15
                </Typography>
                <Typography variant="body1">
                  Enter the final qualified business income (QBI) deduction
                  amount on the applicable line of your tax return, such as:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Form 990-T – Line 9 </li>
                  <li>Form 1040, 1040-SR, or 1040-NR – Line 13a </li>
                  <li>Form 1041 – Line 20</li>
                  <li>Form 1041-N – Line 9 </li>
                  <li>ESBT S-corporation portion – Line 11</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 16
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If you have a{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      qualified business loss,
                    </span>{' '}
                    this amount must be carried forward to the next tax year.
                  </li>
                  <li>
                    The carryforward will reduce future QBI deductions, even if
                    the business that generated the loss no longer exists.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.8, fontWeight: 600 }}>
                  Line 17
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Any unused qualified{' '}
                    <span style={{ fontWeight: 600 }}> REIT</span> or{' '}
                    <span style={{ fontWeight: 600 }}>PTP</span> loss must also
                    be carried forward to the next year.
                  </li>
                  <li>
                    This amount will offset future qualified REIT dividends or
                    PTP income, even if the partnership no longer exists.
                  </li>
                </Box>

                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 2,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                    Embark on a Smooth Form 8995 Filing Journey with TaxZerone
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Complete your 8995 filing requirements with ease!
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form8995`}
                  >
                    E-file Now
                  </Button>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above given information is based on the IRS
                        guidelines. For more details, please refer to the <br />
                        IRS Form 8995{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i8995.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          instruction
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" id="common" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
            className="responsive"
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is the QBI deduction and who is eligible for it?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                      The QBI deduction is a tax deduction that allows
                      pass-through entities to deduct a portion of their
                      qualified business income. This deduction can reduce the
                      taxable income of these entities.
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0, mb: 1 }}>
                      Sole proprietorships, partnerships, S corporations, and
                      trusts are generally eligible for the QBI deduction.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What is the income threshold for using Form 8995?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
                      In 2025, the income threshold for using the simplified
                      Form 8995 is $197,300 for single filers and $394,600 for
                      joint filers.
                    </Typography>
                    <Typography variant="body1">
                      If your income exceeds these amounts, you may need to use
                      Form 8995-A, Qualified Business Income Deduction to
                      calculate the deduction.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Can I use Form 8995 if my business is a rental property?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    In some cases, rental income can qualify for the QBID if the
                    rental activity is considered a trade or business. This
                    determination depends on factors like the regularity and
                    continuity of the rental activity and the level of
                    involvement in managing the property.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  4. Why do I have Form 8995 on my tax return?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    Form 8995 is included in your tax return to calculate your
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Qualified Business Income (QBI)
                    </span>{' '}
                    deduction. If you earned eligible business income from
                    sources like a sole proprietorship, partnership, S
                    corporation, REIT, or PTP, you may qualify for a{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      deduction of up to 20%
                    </span>{' '}
                    of that income.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  5. What qualifies as Qualified Business Income (QBI)?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text" sx={{ mt: 1 }}>
                    Qualified Business Income (QBI) generally includes eligible
                    income, gains, deductions, and losses from qualified trades
                    or businesses operated in the United States. This may
                    include income from sole proprietorships, partnerships, S
                    corporations, and certain estates or trusts that is included
                    in your taxable income for the year.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form8995;
